/* eslint-disable import/no-named-as-default */
import {
  CircularProgress,
  EditIcon,
  FolderIcon,
  GKDataGrid,
  Grid2,
  IconButton,
  NumberIcon,
  ReloadIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "components";
import dayjs from "dayjs";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { numberToCurrency } from "utils";
import {
  PORTFOLIO_GROUP_BY_ID,
  PORTFOLIO_GROUP_HOLDING,
  PORTFOLIO_GROUP_REFRESH_XIRR_BY_ID,
} from "utils/apiRoutes";
import { paletteLightTheme } from "utils/ColorTheme";
import { assetsHoldingsColumns } from "utils/columns";
import { refetchQuery } from "utils/helpers";
import { QueryKeys } from "utils/queryKey";
import useDocumentTitle from "utils/useDocumentTitle";
import EditPortfolioGroupDetails from "./EditPortfolioGroupDetails";

const PortfolioGroupDetails = () => {
  useDocumentTitle("Portfolio Group Details");

  const { id } = useParams<{ id: string }>();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [singleGroupDetails, setSingleGroupDetails] = useState<any>();

  const requestBody = useGenerateQueryParams("portfolioGroupHolding");

  const { data: portfolioGroup, isLoading } = useFetchQuery({
    key: [QueryKeys.PORTFOLIO_GROUP_LIST, id, requestBody],
    route: PORTFOLIO_GROUP_HOLDING(id),
    requestBody,
  });

  const { data: portfolioGroupData } = useFetchQuery({
    key: ["GET_PORTFOLIOGROUP_BY_ID"],
    route: PORTFOLIO_GROUP_BY_ID(id),
  });

  const { isFetching: xirrLoading, refetch: refreshXirr } = useFetchQuery({
    route: PORTFOLIO_GROUP_REFRESH_XIRR_BY_ID(id),
    key: ["GET_PORTFOLIO_XIRR"],
    enabled: false,
    success: () => {
      refetchQuery("GET_PORTFOLIOGROUP_BY_ID");
    },
  });

  return (
    <DashboardLayout title={"Portfolio Group Details"}>
      <Grid2 container spacing={2}>
        <Grid2
          container
          size={12}
          p={2}
          borderRadius={1}
          bgcolor={"var(--grey-bg-100)"}
        >
          <Grid2 size={4.5} container spacing={0.5}>
            <Grid2 size={12} display={"flex"} alignItems={"center"} gap={"4px"}>
              <FolderIcon color="var(--neutral-500)" />
              <Typography
                fontWeight={500}
                color="var(--neutral-500)"
                display={"flex"}
                alignItems={"center"}
                gap={1}
              >
                Portfolio Group:
                <Typography
                  fontWeight={500}
                  color={"var(--black)"}
                  component={"span"}
                >
                  {portfolioGroupData?.data?.name}
                </Typography>
              </Typography>
              <Grid2>
                <IconButton
                  className="outline"
                  onClick={() => {
                    setSingleGroupDetails(portfolioGroupData?.data);
                    setTimeout(() => {
                      setIsModalOpen(true);
                    }, 100);
                  }}
                >
                  <EditIcon height={16} width={16} />
                </IconButton>
              </Grid2>
            </Grid2>
            <Grid2 size={12} display={"flex"} alignItems={"center"} gap={"4px"}>
              <NumberIcon color="var(--neutral-500)" />
              <Typography
                fontWeight={500}
                color="var(--neutral-500)"
                display={"flex"}
                alignItems={"center"}
                gap={1}
              >
                No. of Portfolios:
                <Typography
                  fontWeight={500}
                  color={"var(--black)"}
                  component={"span"}
                >
                  {portfolioGroupData?.data?.portfolios?.length}
                </Typography>
              </Typography>
            </Grid2>
          </Grid2>
        </Grid2>
        <Grid2 size={12}>
          <TableContainer>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell
                    sx={{
                      p: "16px !important",
                      borderBottom: "none",
                      borderRight: `solid 1px ${paletteLightTheme.palette.border.main}`,
                    }}
                    width={"20%"}
                  >
                    <Typography
                      fontWeight={400}
                      fontSize={12}
                      color={"var(--neutral-500)"}
                    >
                      Net Contribution
                    </Typography>
                    <Typography fontWeight={600} fontSize={18}>
                      {numberToCurrency(
                        portfolioGroupData?.data?.details?.netContribution,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      p: "16px !important",
                      borderBottom: "none",
                      borderRight: `solid 1px ${paletteLightTheme.palette.border.main}`,
                    }}
                    width={"20%"}
                  >
                    <Typography
                      fontWeight={400}
                      fontSize={12}
                      color={"var(--neutral-500)"}
                    >
                      Invested Value
                    </Typography>
                    <Typography fontWeight={600} fontSize={18}>
                      {numberToCurrency(
                        portfolioGroupData?.data?.details?.investedValue,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{
                      p: "16px !important",
                      borderBottom: "none",
                      borderRight: `solid 1px ${paletteLightTheme.palette.border.main}`,
                    }}
                    width={"20%"}
                  >
                    <Typography
                      fontWeight={400}
                      fontSize={12}
                      color={"var(--neutral-500)"}
                    >
                      Portfolio Value
                    </Typography>
                    <Typography fontWeight={600} fontSize={18}>
                      {numberToCurrency(
                        portfolioGroupData?.data?.details?.currentValue,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell
                    style={{
                      padding: 16,
                      width: "20%",
                      borderBottom: "none",
                    }}
                  >
                    <Typography
                      fontSize={12}
                      color={"var(-neutral-700)"}
                      display={"flex"}
                      gap={1}
                    >
                      Returns
                    </Typography>
                    <Typography
                      fontWeight={600}
                      fontSize={18}
                      color="var(--black)"
                      display={"flex"}
                      alignItems={"center"}
                      gap={1}
                    >
                      {/* {userData?.showTwrr
                        ? +holdingSummaryDetails?.twrr?.toFixed(2) || "0"
                        : +holdingSummaryDetails?.xirr?.toFixed(2) || "0"} */}
                      {portfolioGroupData?.data?.details?.xirr || "0"}
                      <Typography
                        fontSize={12}
                        fontWeight={500}
                        color="var(--neutral-500)"
                        display="flex"
                        alignItems="center"
                        gap={1}
                        component="span"
                      >
                        (*As of{" "}
                        {portfolioGroupData?.data?.details?.lastXirrRefresh
                          ? dayjs(
                              portfolioGroupData.data.details.lastXirrRefresh,
                            ).format("DD/MM/YYYY")
                          : "N/A"}
                        )
                        <IconButton
                          sx={{ p: 0 }}
                          disabled={xirrLoading}
                          onClick={() => refreshXirr()}
                        >
                          {xirrLoading ? (
                            <CircularProgress size={13} />
                          ) : (
                            <ReloadIcon
                              width={13}
                              style={{ color: "var(--primary-900)" }}
                            />
                          )}
                        </IconButton>
                      </Typography>
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid2>
        <Grid2 size={12}>
          <GKDataGrid
            density="compact"
            loading={isLoading}
            rows={portfolioGroup?.data?.data || []}
            columns={assetsHoldingsColumns}
            pinnedColumns={{
              left: ["name"],
            }}
            zeroHolding
            totalCount={portfolioGroup?.data?.totalCount || 0}
            name="portfolioGroupHolding"
            tableName="portfolio_group_holding_list"
            exportFile={{
              path: PORTFOLIO_GROUP_HOLDING(id),
              exportName: "PortfolioGroupHolding",
            }}
          />
        </Grid2>
      </Grid2>

      {isModalOpen && (
        <EditPortfolioGroupDetails
          open={isModalOpen}
          setOpen={setIsModalOpen}
          portfolioGroup={singleGroupDetails}
        />
      )}
    </DashboardLayout>
  );
};

export default PortfolioGroupDetails;
